// Cache sprite bangunan (kunci performa zoom/geser) + pra-render bucket zoom tetangga.
import { TILE_W, TILE_H } from "./iso";
import { refHeight } from "./paint";
import { paintBuildingTo } from "./buildingPaint";

import { siapGambarSponsor } from "./sponsorGambar";

export function createSpriteCache({ getDpr, getZoom, getNightQ, minZoom, maxZoom }) {
  // ---- SPRITE CACHE BANGUNAN (kunci performa zoom/geser) ----
  // Melukis paintCode (ribuan perintah canvas) untuk 100+ bangunan tiap rebuild dunia memakan 100–250 ms
  // saat zoom-out (semua bangunan terlihat) → terasa patah-patah. Kini tiap bangunan dilukis SEKALI per
  // (bid|varian|rotasi|nama edit|bucket zoom|tingkat malam) ke kanvas kecil, lalu hanya di-blit (drawImage)
  // dengan skala f/bucket (±10%). Bucket zoom bertingkat ×1.2; tingkat malam 5 langkah (jendela menyala bertahap).
  const SPRITE_STEP = 1.2, SPRITE_BUDGET = 36e6;  // ≈36 MP piksel total cache (~145 MB) — aman untuk HP; yang tertua dibuang
  const bucketZoom = z => Math.pow(SPRITE_STEP, Math.round(Math.log(z) / Math.log(SPRITE_STEP)));
  const bldSprites = new Map(); let spriteBytes = 0;
  const profile = { roads: 0, buildings: 0, nBuildings: 0, total: 0, zoom: 0, spriteHits: 0, spriteMiss: 0 };
  // Sidik jari paintCode per objek def (WeakMap) → sprite ikut diperbarui saat Studio AI menerapkan kode baru
  const defSigs = new WeakMap();
  function defSig(def) {
    if (!def) return "0";
    let s = defSigs.get(def);
    if (s) return s;
    let h = 2166136261;
    const src = (def.paintCode || "") + (def.variants || []).map(v => v.vid + (v.paintCode || "")).join("|");
    for (let i = 0; i < src.length; i++) { h ^= src.charCodeAt(i); h = Math.imul(h, 16777619); }
    s = (h >>> 0).toString(36); defSigs.set(def, s);
    return s;
  }
  function bldSprite(b, def, bz, nq) {
    const rot = b.rot || 0;
    const gid = b.sponsor?.gambar_id || "";
    const key = `${def?.bid}|${defSig(def)}|${b.vid || ""}|${rot}|${bz.toFixed(4)}|${nq}|${b.signEdit ? JSON.stringify(b.signEdit) : ""}|${gid}${gid && siapGambarSponsor(gid) ? ":ok" : ""}`;
    let sp = bldSprites.get(key);
    if (sp) { profile.spriteHits++; return sp; }
    profile.spriteMiss++;
    let w = def?.w || 1, h = def?.h || 1;
    if (rot % 2 === 1) [w, h] = [h, w];
    const z = bz, hw = (TILE_W / 2) * z, hh = (TILE_H / 2) * z;
    const height = refHeight(def?.floors ?? 1, w, h, z);
    // bbox tapak + tinggi + margin detail di luar prisma (pohon halaman, papan nama, kanopi)
    const mx = TILE_W * z * 0.8, my = TILE_H * z * 2 + height * 0.6;
    const x0 = Math.floor(-h * hw - mx), y0 = Math.floor(-height - my);
    const sw = Math.ceil(w * hw + mx) - x0, sh = Math.ceil((w + h) * hh + TILE_H * z * 1.5) - y0;
    const cv = document.createElement("canvas");
    const dpr = getDpr();
    cv.width = Math.max(1, Math.ceil(sw * dpr)); cv.height = Math.max(1, Math.ceil(sh * dpr));
    const g = cv.getContext("2d");
    g.scale(dpr, dpr); g.translate(-x0, -y0);
    const proj = (gx, gy) => [((gx - b.x) - (gy - b.y)) * hw, ((gx - b.x) + (gy - b.y)) * hh];
    paintBuildingTo(g, b, def, proj, z, nq);
    sp = { cv, dx: x0 / z, dy: y0 / z, sw: sw / z, sh: sh / z, px: cv.width * cv.height };
    spriteBytes += sp.px;
    for (const [k, v] of bldSprites) { if (spriteBytes <= SPRITE_BUDGET) break; bldSprites.delete(k); spriteBytes -= v.px; } // buang yang tertua
    bldSprites.set(key, sp);
    return sp;
  }
  // Pra-render sprite untuk bucket zoom tetangga (±1 langkah) saat idle, dipotong 8 ms per irisan —
  // langkah zoom berikutnya tidak lagi "dingin" (tanpa hitch melukis paintCode).
  let prewarmT = null;
  function schedulePrewarm(items) {
    clearTimeout(prewarmT);
    prewarmT = setTimeout(() => {
      const bz = bucketZoom(getZoom()), nq = getNightQ(), queue = [];
      if (bz > 1.05) return;   // zoom dekat: sprite besar & bangunan terlihat sedikit — tidak perlu pra-render (hemat memori)
      for (const t of [bz * SPRITE_STEP, bz / SPRITE_STEP]) if (t >= minZoom * 0.9 && t <= maxZoom * 1.1) for (const it of items) queue.push([it, t]);
      const slice = () => {
        const t0 = performance.now();
        while (queue.length && performance.now() - t0 < 8) { const [it, t] = queue.shift(); bldSprite(it.b, it.def, t, nq); }
        if (queue.length) prewarmT = setTimeout(slice, 40);
      };
      slice();
    }, 220);
  }
  return { bldSprite, bucketZoom, schedulePrewarm, profile };
}
